import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Pencil, X, Check, ChevronDown, ChevronUp, AlertTriangle, Heart, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { useMedicalProfile, useUpsertMedicalProfile, type MedicalProfileItem } from "@/hooks/api/useMedicalProfile";
import { toast } from "sonner";

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  items: MedicalProfileItem[];
  emptyText: string;
  onSave: (items: MedicalProfileItem[]) => Promise<void>;
  isSaving: boolean;
}

function ProfileSection({ title, icon, items, emptyText, onSave, isSaving }: SectionProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<MedicalProfileItem[]>(items);
  const [newName, setNewName] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const handleStartEdit = () => {
    setDraft([...items]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setDraft([...items]);
    setNewName("");
    setNewNotes("");
    setIsEditing(false);
  };

  const handleAdd = () => {
    if (!newName.trim()) return;
    setDraft((prev) => [...prev, { name: newName.trim(), notes: newNotes.trim() || null }]);
    setNewName("");
    setNewNotes("");
  };

  const handleRemove = (idx: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    await onSave(draft);
    setIsEditing(false);
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-3 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
        onClick={() => setIsExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2 font-medium text-sm">
          {icon}
          {title}
          {items.length > 0 && (
            <span className="text-xs bg-primary/10 text-primary rounded-full px-1.5 py-0.5 font-mono">
              {items.length}
            </span>
          )}
        </div>
        {isExpanded ? <ChevronUp className="h-3 w-3 text-muted-foreground" /> : <ChevronDown className="h-3 w-3 text-muted-foreground" />}
      </button>

      {isExpanded && (
        <div className="px-3 py-2 space-y-2">
          {!isEditing ? (
            <>
              {items.length === 0 ? (
                <p className="text-xs text-muted-foreground py-1">{emptyText}</p>
              ) : (
                <ul className="space-y-1">
                  {items.map((item, i) => (
                    <li key={i} className="text-xs">
                      <span className="font-medium">{item.name}</span>
                      {item.notes && <span className="text-muted-foreground ml-1">— {item.notes}</span>}
                    </li>
                  ))}
                </ul>
              )}
              <Button
                size="sm"
                variant="ghost"
                className="h-6 text-xs gap-1 px-1 -ml-1"
                onClick={handleStartEdit}
              >
                <Pencil className="h-3 w-3" />
                {t("common.edit")}
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              {draft.map((item, i) => (
                <div key={i} className="flex items-start gap-1.5">
                  <div className="flex-1 text-xs">
                    <span className="font-medium">{item.name}</span>
                    {item.notes && <span className="text-muted-foreground ml-1">— {item.notes}</span>}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-5 w-5 p-0 text-red-400 hover:text-red-500 shrink-0"
                    onClick={() => handleRemove(i)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
              <div className="space-y-1 pt-1 border-t">
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={t("medicalProfile.namePlaceholder")}
                  className="h-7 text-xs"
                  onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                />
                <Textarea
                  value={newNotes}
                  onChange={(e) => setNewNotes(e.target.value)}
                  placeholder={t("medicalProfile.notesPlaceholder")}
                  rows={1}
                  className="text-xs min-h-0 resize-none"
                />
                <Button size="sm" variant="outline" className="h-6 text-xs gap-1 w-full" onClick={handleAdd} disabled={!newName.trim()}>
                  <Plus className="h-3 w-3" />
                  {t("medicalProfile.add")}
                </Button>
              </div>
              <div className="flex gap-1.5 pt-1">
                <Button size="sm" className="h-6 text-xs gap-1 flex-1" onClick={handleSave} disabled={isSaving}>
                  {isSaving ? <Spinner className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                  {t("common.save")}
                </Button>
                <Button size="sm" variant="outline" className="h-6 text-xs" onClick={handleCancel}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  patientId: string;
  patientName?: string;
}

export function PatientLifetimeSidebar({ patientId, patientName }: Props) {
  const { t } = useTranslation();
  const { data: profile, isLoading } = useMedicalProfile(patientId);
  const upsertMutation = useUpsertMedicalProfile();

  const handleSaveAllergies = async (allergies: MedicalProfileItem[]) => {
    try {
      await upsertMutation.mutateAsync({ patientId, allergies });
      toast.success(t("medicalProfile.saved"));
    } catch {
      toast.error(t("medicalProfile.saveError"));
    }
  };

  const handleSaveChronic = async (chronicConditions: MedicalProfileItem[]) => {
    try {
      await upsertMutation.mutateAsync({ patientId, chronicConditions });
      toast.success(t("medicalProfile.saved"));
    } catch {
      toast.error(t("medicalProfile.saveError"));
    }
  };

  const handleSaveSurgical = async (surgicalHistory: MedicalProfileItem[]) => {
    try {
      await upsertMutation.mutateAsync({ patientId, surgicalHistory });
      toast.success(t("medicalProfile.saved"));
    } catch {
      toast.error(t("medicalProfile.saveError"));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Heart className="h-4 w-4 text-red-500" />
        <h3 className="text-sm font-semibold">{patientName ?? t("medicalProfile.title")}</h3>
      </div>
      <p className="text-xs text-muted-foreground">{t("medicalProfile.subtitle")}</p>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner className="h-4 w-4" />
        </div>
      ) : (
        <div className="space-y-2">
          <ProfileSection
            title={t("medicalProfile.allergies")}
            icon={<AlertTriangle className="h-3.5 w-3.5 text-amber-500" />}
            items={profile?.allergies ?? []}
            emptyText={t("medicalProfile.noAllergies")}
            onSave={handleSaveAllergies}
            isSaving={upsertMutation.isPending}
          />
          <ProfileSection
            title={t("medicalProfile.chronicConditions")}
            icon={<Heart className="h-3.5 w-3.5 text-red-400" />}
            items={profile?.chronicConditions ?? []}
            emptyText={t("medicalProfile.noChronicConditions")}
            onSave={handleSaveChronic}
            isSaving={upsertMutation.isPending}
          />
          <ProfileSection
            title={t("medicalProfile.surgicalHistory")}
            icon={<Scissors className="h-3.5 w-3.5 text-blue-500" />}
            items={profile?.surgicalHistory ?? []}
            emptyText={t("medicalProfile.noSurgicalHistory")}
            onSave={handleSaveSurgical}
            isSaving={upsertMutation.isPending}
          />
        </div>
      )}
    </div>
  );
}
